/**
 * Modal popup showing team (doubles) stats for multiple players. Closable; no redirect.
 * Clicking a player name opens the profile stats modal.
 */

import React, { useState, useEffect } from 'react';
import * as readApi from '../api/readApi';
import { usePlayerCache } from '../contexts/PlayerCacheContext';
import { useGame } from '../contexts/GameContext';
import { getUserDisplayName } from '../utils/nameFormat';
import CachedAvatar from './CachedAvatar';
import { useStatsModal } from '../contexts/StatsModalContext';
import './TeamStatsModal.css';

function PlayerBlock({ userId, playersMap, statsDoc, enabledGames }) {
  const { openProfileModal } = useStatsModal();
  const displayName = playersMap[userId] ? getUserDisplayName(playersMap[userId]) : (playersMap[userId]?.displayName || 'Unknown');
  const avatarUrl = playersMap[userId]?.photoURL || playersMap[userId]?.avatarURL || '';

  const getStatsForGame = (gameId) => {
    if (!statsDoc) return null;
    const gs = (statsDoc.gameStats || {})[gameId];
    if (gs) {
      const mw = gs.matchesWon ?? 0;
      const ml = gs.matchesLost ?? 0;
      const total = mw + ml;
      return total > 0 ? { matchesWon: mw, matchesLost: ml, totalPoints: gs.totalPoints ?? 0, totalMatches: total, winRate: ((mw / total) * 100).toFixed(1) } : null;
    }
    return null;
  };

  const gamesToShow = Array.isArray(enabledGames) ? enabledGames : [];
  const rows = gamesToShow.map((game) => ({ game, stats: getStatsForGame(game.id) })).filter((r) => r.stats && r.stats.totalMatches > 0);

  return (
    <div className="team-stats-modal-player-block">
      <button
        type="button"
        onClick={() => openProfileModal(userId)}
        className="team-stats-modal-player-identity"
      >
        <CachedAvatar url={avatarUrl} name={displayName} className="team-stats-modal-player-avatar" alt={displayName} />
        <span className="team-stats-modal-player-name">{displayName}</span>
      </button>
      {rows.length === 0 ? (
        <p className="team-stats-modal-no-stats">No match stats yet.</p>
      ) : (
        <table className="team-stats-modal-mini-table">
          <thead>
            <tr>
              <th>Game</th>
              <th>W</th>
              <th>L</th>
              <th>Win%</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ game, stats }) => (
              <tr key={game.id}>
                <td>{game.icon} {game.name}</td>
                <td>{stats.matchesWon}</td>
                <td>{stats.matchesLost}</td>
                <td>{stats.winRate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function TeamStatsModal() {
  const { teamPlayerIds, closeModal } = useStatsModal();
  const { getPlayers } = usePlayerCache();
  const { games: enabledGames } = useGame();
  const [playersMap, setPlayersMap] = useState({});
  const [statsByPlayer, setStatsByPlayer] = useState({});
  const [loading, setLoading] = useState(true);

  const playerIds = Array.isArray(teamPlayerIds) ? teamPlayerIds : [];
  const playerIdsKey = playerIds.length ? playerIds.join(',') : '';

  useEffect(() => {
    if (playerIds.length === 0) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const map = await getPlayers(playerIds);
        if (cancelled) return;
        setPlayersMap(map);
        const stats = {};
        await Promise.all(
          playerIds.map(async (uid) => {
            if (cancelled) return;
            const s = await readApi.getPlayerStats(uid);
            if (!cancelled) stats[uid] = s;
          })
        );
        if (!cancelled) setStatsByPlayer(stats);
      } catch (err) {
        console.error('Error fetching team stats for modal:', err);
        if (!cancelled) setPlayersMap({});
        setStatsByPlayer({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when id list changes (playerIdsKey); getPlayers identity would cause loop
  }, [playerIdsKey]);

  if (playerIds.length === 0) return null;

  return (
    <div className="team-stats-modal-overlay" onClick={closeModal} role="dialog" aria-modal="true" aria-labelledby="team-stats-modal-title">
      <div className="team-stats-modal" onClick={(e) => e.stopPropagation()}>
        <div className="team-stats-modal-header">
          <h2 id="team-stats-modal-title" className="team-stats-modal-title">Team stats</h2>
          <button type="button" onClick={closeModal} className="team-stats-modal-close" aria-label="Close">
            ×
          </button>
        </div>
        <div className="team-stats-modal-body">
          {loading ? (
            <div className="loading">Loading…</div>
          ) : (
            <div className="team-stats-modal-players">
              {playerIds.map((userId) => (
                <PlayerBlock
                  key={userId}
                  userId={userId}
                  playersMap={playersMap}
                  statsDoc={statsByPlayer[userId]}
                  enabledGames={enabledGames}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
